import {
  FIRST_ORDERED_BOARD_CODES,
  FREE_MODE_DISTRIBUTION, MULTIPLE_ROOMS,
} from '../constants';
import type { Occupancy } from '../FlowTypes';

export const paramToDistribution = (distriParam) => {
  if (distriParam === FREE_MODE_DISTRIBUTION) {
    return FREE_MODE_DISTRIBUTION;
  }
  return distriParam.split(',').map((distriData) => {
    const tokens = distriData.split('-');
    const toNum = (str, defaultVal) => (parseInt(str, 10) || defaultVal);
    return { adl: toNum(tokens[0], 2), chd: toNum(tokens[1], 0), enf: toNum(tokens[2], 0) };
  });
};

export const distributionToParam = (distris) => (
  distris.map((dist) => (`${dist.adl || 0}-${dist.chd || 0}-${dist.enf || 0}`)).join(',')
);

export const getOccupancyLabel = ({ adults, childs, ages }: Occupancy, context: Object) => {
  const adlLabel = `${adults} ${adults === 1 ? context.t('adult') : context.t('adults')}`;
  if (childs === 0) {
    return adlLabel;
  }
  return `${adlLabel} + ${childs} ${childs === 1 ? context.t('child') : context.t('children')} (${ages.join(', ')} ${context.t('years')})`;
};

export const getUseKey = ({ adults, childs, infants }) => `${adults}-${childs}-${infants}`;

export const toUseWithAges = (hotel, useRequested) => {
  const children = useRequested ? useRequested.childs : 0;
  const infants = useRequested ? useRequested.infants : 0;
  const totalChildren = children + infants;
  let ages;
  if (useRequested) {
    if (useRequested.ages && useRequested.ages.length === totalChildren) {
      // If we receive valid ages for all the children, then we'll use those ages
      ages = useRequested.ages;
    } else {
      // If we receive missing data, then we need to add the ages for each child
      ages = [];
      while (ages.length < totalChildren) {
        const minChildAge = hotel.min_child_age || 2;
        const maxChildAge = hotel.max_child_age || 12;
        ages.push(ages.length < children ? maxChildAge : minChildAge - 1);
      }
    }
  }
  return {
    adults: useRequested ? useRequested.adults : 0,
    childs: totalChildren || 0,
    ages,
  };
};

export const isValidAge = (hotel, paxKey, age) => {
  if (paxKey === 'infants') {
    return age >= 0 && age < hotel.min_child_age;
  }
  return age >= hotel.min_child_age && age <= hotel.max_child_age;
};

const findCheapestRoom = (roomItems) => {
  let cheapestItem = null;
  roomItems.forEach((roomItem) => {
    if (!cheapestItem || roomItem.amount < cheapestItem.amount) {
      cheapestItem = roomItem;
    }
  });
  return cheapestItem;
};

const findCheapestOption = (rooms, currencyCode) => {
  let cheapest = null;
  rooms.forEach((room) => {
    room.rates.forEach((rate) => {
      rate.boards.forEach((board) => {
        const currentAmount = board.amount && board.amount[currencyCode] ? board.amount[currencyCode].amount : null;
        if (!cheapest || (currentAmount && currentAmount < cheapest.bestPrice)) {
          cheapest = {
            singleRoom: room,
            boardCode: board.code,
            rateCode: rate.code,
            bestPrice: currentAmount,
          };
        }
      });
    });
  });
  return cheapest;
};

const buildAllCombinations = (hotel, currencyCode) => {
  const roomCombinations = {};
  Object.keys(hotel.rooms).forEach((roomKey) => {
    hotel.rooms[roomKey].forEach((hotelRoom) => {
      hotelRoom.rates.forEach((rate) => {
        rate.boards.forEach((board) => {
          const roomCombinationKey = `${rate.code}__${board.code}`;
          const existentRoomCombination = roomCombinations[roomCombinationKey];
          const roomItem = {
            data: hotelRoom,
            amount: board.amount[currencyCode].amount,
          };
          if (existentRoomCombination && existentRoomCombination[roomKey]) {
            existentRoomCombination[roomKey].push(roomItem);
          } else if (existentRoomCombination) {
            existentRoomCombination[roomKey] = [roomItem];
          } else {
            roomCombinations[roomCombinationKey] = {
              [roomKey]: [roomItem],
            };
          }
        });
      });
    });
  });
  return roomCombinations;
};

export const combinableRooms = (hotel) => {
  const combinable = {};
  Object.keys(hotel.rooms).forEach((roomKey) => {
    combinable[roomKey] = {};
    hotel.rooms[roomKey].forEach((room) => {
      combinable[roomKey][room.code] = combinableRoomOptions(hotel, room);
    });
  });
  return combinable;
};

const combinableRoomOptions = (hotel, room) => {
  const combinableOptions = {};
  room.rates.forEach((rate) => {
    combinableOptions[rate.code] = {};
    rate.boards.forEach((board) => {
      combinableOptions[rate.code][board.code] = [];
    });
  });
  const otherRoomsKeys = Object.keys(hotel.rooms).filter((roomKey) => roomKey !== room.request_id.toString());
  otherRoomsKeys.forEach((otherRoomKey) => {
    hotel.rooms[otherRoomKey].forEach((otherRoom) => {
      const validRates = otherRoom.rates.filter((rate) => combinableOptions[rate.code]);
      validRates.forEach((rate) => {
        const validBoards = rate.boards.filter((board) => combinableOptions[rate.code][board.code]);
        validBoards.forEach((board) => {
          combinableOptions[rate.code][board.code].push(otherRoom.code);
        });
      });
    });
  });
  const validCombinationOptions = {};
  let hasValid = false;
  Object.keys(combinableOptions).forEach((rateCode) => {
    const boards = combinableOptions[rateCode];
    let validRoomCodes = [];
    const validBoardCodes = [];
    Object.keys(boards).forEach((boardCode) => {
      if (boards[boardCode].length > 0) {
        validBoardCodes.push(boardCode);
        validRoomCodes = boards[boardCode];
      }
    });
    if (validRoomCodes.length > 0 && validBoardCodes.length > 0) {
      hasValid = true;
      validCombinationOptions[rateCode] = {
        validBoardCodes,
        validRoomCodes,
      };
    }
  });
  return hasValid ? validCombinationOptions : null;
};

/**
 * Returns the cheapest hotel option. It can be of one or two rooms, depending on the availabilityHotel response
 * @param hotel
 * @param currencyCode
 */
export const getCheapestHotelOption = (hotel, currencyCode) => {
  const isOneRoom = Object.keys(hotel.rooms).length === 1;
  if (isOneRoom) {
    return findCheapestOption(hotel.rooms[MULTIPLE_ROOMS.FIRST], currencyCode);
  }
  return getCheapestCombination(hotel, currencyCode);
};

/**
 * Generates the cheapest option for any two rooms that have the same board and rate
 * The combination must have available quota, so if it's the same room twice, it needs to have a minimum quota of 2
 *
 * @param hotel Hotel, which contains the information of the total available room quotas
 * @param currencyCode Currency in which to show the prices
 * @returns information of the cheapest two room combination
 */
export const getCheapestCombination = (hotel, currencyCode) => {
  const allRoomCombinations = buildAllCombinations(hotel, currencyCode);
  let cheapestCombination = null;
  Object.keys(allRoomCombinations).forEach((combinationKey) => {
    const combination = allRoomCombinations[combinationKey];
    if (Object.keys(combination).length >= Object.keys(hotel.rooms).length) { // check if there are enough rooms
      const cheapestRooms = [];
      Object.keys(hotel.rooms).forEach((roomKey) => {
        const cheapestRoom = findCheapestRoom(combination[roomKey]);
        const roomAlreadyExits = cheapestRooms.some((room) => room.code === cheapestRoom.data.code);
        const roomOccurrences = cheapestRooms.filter((room) => room.code === cheapestRoom.data.code).length;
        if (!roomAlreadyExits || (roomAlreadyExits && (cheapestRoom.data.quota >= roomOccurrences))) {
          cheapestRooms[roomKey] = cheapestRoom;
        }
      });
      let roomsAmount = 0;
      cheapestRooms.forEach((room) => {
        roomsAmount += room.amount;
      });
      const isBetterPrice = cheapestCombination === null || roomsAmount < cheapestCombination.bestPrice;
      if (isBetterPrice) {
        cheapestCombination = {
          rateCode: combinationKey.split('__')[0],
          boardCode: combinationKey.split('__')[1],
          bestPrice: roomsAmount,
        };
        cheapestCombination.bestRoomCodes = {};
        cheapestRooms.forEach((room, index) => {
          cheapestCombination.bestRoomCodes[index] = room.data.code;
        });
      }
    }
  });
  return cheapestCombination;
};

const getMandatoryRoomSelection = (room, multipleRoomSelection, isEditingBooking) => {
  if (isEditingBooking === false && room.request_id === MULTIPLE_ROOMS.FIRST) {
    return null;
  }
  // When selecting the second room, we must make sure the board and rate is the same
  const firstValidRoomKey = ['1', '2', '3', '4'].find((key) => multipleRoomSelection[key] !== null);
  const baseSelection = multipleRoomSelection[firstValidRoomKey];
  return baseSelection ? {
    rateCode: baseSelection.rateCode,
    boardCode: baseSelection.boardCode,
    availId: baseSelection.availId,
    isMandatory: true,
  } : null;
};

const getFirstOrderedBoardCode = (boards) => {
  for (let i = 0; i <= FIRST_ORDERED_BOARD_CODES.length; i++) {
    if (boards.some((rateBoard) => rateBoard.code === FIRST_ORDERED_BOARD_CODES[i])) {
      return FIRST_ORDERED_BOARD_CODES[i];
    }
  }
  return null;
};

export const getDefaultRoomSelection = (room, multipleRoomSelection, isMobile, isEditingBooking, cartHotel) => {
  // If we are forced to a certain selection, then no need to check the cheapest option
  const mandatorySelection = getMandatoryRoomSelection(room, multipleRoomSelection, isEditingBooking);
  if (mandatorySelection) {
    return mandatorySelection;
  }

  let cheapestOption = {};
  let cheapestOptions = [];
  let cheapestPrice = 100000000;

  let firstBoard = room.rates[0].boards[0];
  // Primero validamos si existen cuartos en el carrito para forzar elegir
  // el mismo board que tiene el primer cuarto del carrito
  // Por convención, todos los cuartos tienen el mismo board, así que con
  // elegir el primero es suficiente
  if (cartHotel.rooms.length > 0) {
    for (let i = 0; i < room.rates[0].boards.length; i++) {
      if (room.rates[0].boards[i].code === cartHotel.rooms[0].board.code) {
        firstBoard = room.rates[0].boards[i];
      }
    }
  }

  const compareCurrency = Object.keys(firstBoard.amount)[0]; // we take any currency to compare prices across options

  room.rates.forEach((rate) => {
    rate.boards.forEach((board) => {
      if (board.code === firstBoard.code) {
        const boardRateAmount = board.amount[compareCurrency].amount;
        const roundAmount = Number(((Math.floor(100 * boardRateAmount)) / 100).toFixed(2));
        const newOption = {
          rateCode: rate.code,
          // Client wants specific board on first position for mobile
          boardCode: board.code,
          availId: board.avail_id,
          isMandatory: false,
        };

        if (roundAmount === cheapestPrice) {
          cheapestOptions.push(newOption);
        } else if (roundAmount < cheapestPrice) {
          cheapestOptions = [newOption];
          cheapestPrice = roundAmount;
        }
      }
    });
  });

  // If only one option has the cheapest price, then we return that one
  if (cheapestOptions.length === 1) {
    cheapestOption = cheapestOptions[0];
  } else {
    // If there is more than one option with the cheapest price, we look at the rate sort
    const hasBetterRateCode = (rate) => cheapestOptions.find((option) => option.rateCode === rate.code) !== undefined;
    const betterRate = room.rates.find(hasBetterRateCode);

    // Now we take the cheapest option for the first sorted rate
    cheapestOption = cheapestOptions.find((option) => option.rateCode === betterRate.code);
  }

  if (isMobile) {
    cheapestOption.boardCode = getFirstOrderedBoardCode(room.rates[0].boards);
  }

  return cheapestOption;
};
